import { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../store/store";
import {
  addAlert,
  acknowledgeAlertLocal,
  dismissAlertLocal,
  updatePreferences,
  markAllAsRead,
  fetchAlerts,
  acknowledgeAlert,
  dismissAlert,
} from "../store/alertSlice";
import { AlertData, AlertPreferences } from "../store/alertSlice";
import { useWebSocket } from "./useWebSocket";
import { useAuth } from "../contexts/AuthContext";

export const useAlerts = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useAuth();
  const { alerts, preferences, unreadCount, loading, error } = useSelector(
    (state: RootState) => state.alerts,
  );

  // WebSocket connection for real-time alerts
  const { isConnected, sendMessage } = useWebSocket({
    url: `${import.meta.env.VITE_WS_URL || "ws://localhost:4000"}/alerts`,
    onMessage: (message) => {
      if (message.type === "NEW_ALERT") {
        dispatch(addAlert(message.payload));
      } else if (message.type === "ALERT_UPDATE") {
        // Handle alert updates
        console.log("Alert update received:", message.payload);
      }
    },
    onError: (error) => {
      // Temporarily disable WebSocket error logging to reduce console spam
      // console.error('Alert WebSocket error:', error);
    },
    enabled: false, // Temporarily disable WebSocket to stop console spam
  });

  // Fetch alerts on mount
  useEffect(() => {
    if (user?.id) {
      dispatch(fetchAlerts(user.id));
    }
  }, [dispatch, user?.id]);

  // Subscribe to alert categories
  useEffect(() => {
    if (isConnected && user?.id) {
      sendMessage({
        type: "SUBSCRIBE",
        userId: user.id,
        categories: preferences.categories,
        minSeverity: preferences.minSeverity,
      });
    }
  }, [
    isConnected,
    user?.id,
    preferences.categories,
    preferences.minSeverity,
    sendMessage,
  ]);

  const acknowledgeAlertHandler = useCallback(
    async (alertId: string) => {
      if (!user?.id) return;

      // Optimistic update
      dispatch(acknowledgeAlertLocal(alertId));

      try {
        await dispatch(acknowledgeAlert({ alertId, userId: user.id })).unwrap();
      } catch (error) {
        console.error("Failed to acknowledge alert:", error);
        // Could revert optimistic update here if needed
      }
    },
    [dispatch, user?.id],
  );

  const dismissAlertHandler = useCallback(
    async (alertId: string) => {
      if (!user?.id) return;

      // Optimistic update
      dispatch(dismissAlertLocal(alertId));

      try {
        await dispatch(dismissAlert({ alertId, userId: user.id })).unwrap();
      } catch (error) {
        console.error("Failed to dismiss alert:", error);
      }
    },
    [dispatch, user?.id],
  );

  const updatePreferencesHandler = useCallback(
    (newPreferences: Partial<AlertPreferences>) => {
      dispatch(updatePreferences(newPreferences));
    },
    [dispatch],
  );

  const markAllAsReadHandler = useCallback(() => {
    dispatch(markAllAsRead());
  }, [dispatch]);

  const getFilteredAlerts = useCallback(() => {
    return alerts.filter((alert) => {
      // Filter by category
      if (!preferences.categories.includes(alert.category)) {
        return false;
      }

      // Filter by severity
      if (alert.severity < preferences.minSeverity) {
        return false;
      }

      // Filter out dismissed alerts
      if (alert.dismissed) {
        return false;
      }

      return true;
    });
  }, [alerts, preferences]);

  const getCriticalAlerts = useCallback(() => {
    return alerts.filter(
      (alert) => alert.severity >= 4 && !alert.dismissed && !alert.acknowledged,
    );
  }, [alerts]);

  const getUnreadAlerts = useCallback(() => {
    return alerts.filter((alert) => !alert.acknowledged && !alert.dismissed);
  }, [alerts]);

  return {
    // State
    alerts,
    preferences,
    unreadCount,
    loading,
    error,
    isConnected,

    // Derived values as memoized functions
    getFilteredAlerts: getFilteredAlerts,
    getCriticalAlerts: getCriticalAlerts,
    getUnreadAlerts: getUnreadAlerts,

    // Actions
    acknowledgeAlert: acknowledgeAlertHandler,
    dismissAlert: dismissAlertHandler,
    updatePreferences: updatePreferencesHandler,
    markAllAsRead: markAllAsReadHandler,

    // Utility functions (derived on demand via getters, not directly returned)
    // hasCriticalAlerts and hasUnreadAlerts will be computed in AlertManager or wherever needed
  };
};
